import { UserRoleEnum, normalizeRole } from "@/types/role.type";
import {
  RISK_STATUS,
  normalizeRiskStatus,
  type RiskStatusValue,
} from "@/types/riskStatus.type";

export type RiskImpact = "High" | "Medium" | "Low";
export type RiskProbability = "High" | "Medium" | "Low";
export type RiskStatus = RiskStatusValue;
export type RiskLevel = "High" | "Medium" | "Low";
export type RiskSource =
  | "Manual"
  | "EDC"
  | "CDMS"
  | "Monitor Tracker"
  | "Regulatory"
  | "eTMF"
  | "IWRS"
  | "Finance";

export const RISK_CATEGORIES = [
  "Enrollment",
  "Site Performance",
  "Monitoring",
  "Data Quality",
  "Protocol Compliance",
  "Regulatory",
  "Safety",
  "Drug Supply",
  "Financial",
  "TMF Documentation",
  "Operational Timeline",
  "Vendor / Partner",
  "Technology",
  "Quality / CAPA",
] as const;

export type RiskCategory = (typeof RISK_CATEGORIES)[number];

export const IMPACT_SCORE: Record<RiskImpact, number> = {
  High: 3,
  Medium: 2,
  Low: 1,
};

export const PROBABILITY_SCORE: Record<RiskProbability, number> = {
  High: 3,
  Medium: 2,
  Low: 1,
};

export function calcRiskScore(
  impact: RiskImpact,
  probability: RiskProbability,
): number {
  return IMPACT_SCORE[impact] * PROBABILITY_SCORE[probability];
}

export function getRiskLevel(score: number): RiskLevel {
  if (score >= 7) {
    return "High";
  }
  if (score >= 4) {
    return "Medium";
  }
  return "Low";
}

export interface RiskActivityEntry {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  note?: string;
}

/**
 * Raw shape of an activity entry returned by
 * GET /api/core/v1/studies/{studyId}/risks/{riskId}/activity
 */
export interface ApiActivityEntry {
  id: string;
  action: string; // e.g. "STATUS_CHANGE", "RISK_CREATED"
  fieldChanged?: string;
  previousValue?: string;
  newValue?: string;
  user?: string; // display name (legacy)
  userName?: string; // display name (preferred)
  userId?: string; // UUID fallback
  timestamp?: string; // ISO 8601
  createdAt?: string; // alternative field name from backend
  note?: string;
  notes?: string; // alternative field name
  metadata?: Record<string, unknown>;
}

const _ACTIVITY_ACTION: Record<string, string> = {
  RISK_CREATED: "Risk Created",
  STATUS_CHANGE: "Status Changed",
  STATUS_UPDATED: "Status Updated",
  RISK_UPDATED: "Risk Updated",
  RISK_CLOSED: "Risk Closed",
  COMMENT_ADDED: "Comment Added",
  MITIGATION_UPDATED: "Mitigation Updated",
  RISK_ASSIGNED: "Risk Assigned",
  RISK_ESCALATED: "Risk Escalated",
};

// Maps API enum values (OPEN, IN_PROGRESS…) to display labels
const _VALUE_LABEL: Record<string, string> = {
  OPEN: RISK_STATUS.OPEN,
  IN_PROGRESS: RISK_STATUS.IN_PROGRESS,
  UNDER_REVIEW: RISK_STATUS.UNDER_REVIEW,
  MITIGATED: RISK_STATUS.MITIGATED,
  CLOSED: RISK_STATUS.CLOSED,
  OVERDUE: RISK_STATUS.OVERDUE,
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

export function fromApiActivity(e: ApiActivityEntry): RiskActivityEntry {
  // Prefer userName (new field) → user → truncated userId
  const displayUser = e.userName ?? "Unknown User";

  // When a newValue is present, show it as the action label (e.g. "→ In Progress")
  const actionLabel = e.newValue
    ? `→ ${_VALUE_LABEL[e.newValue] ?? e.newValue}`
    : (_ACTIVITY_ACTION[e.action] ?? e.action);

  return {
    id: e.id,
    action: actionLabel,
    user: displayUser,
    timestamp: e.timestamp ?? e.createdAt ?? new Date().toISOString(),
    note: e.note ?? e.notes,
  };
}

export interface Risk {
  id: string;
  riskNumber?: string;
  title: string;
  description: string;
  category: RiskCategory;
  study: string;
  studyId: string;
  site: string;
  siteId: string;
  impact: RiskImpact;
  probability: RiskProbability;
  riskScore: number;
  riskLevel: RiskLevel;
  owner: string;
  ownerId?: string;
  ownerInitials: string;
  status: RiskStatus;
  createdDate: string;
  dueDate: string;
  mitigationPlan: string;
  targetResolutionDate: string;
  sourceModule: RiskSource;
  activityLog: RiskActivityEntry[];
}

// ─── API Response Types ──────────────────────────────────────────────────────

/** Item in a status/category/impact breakdown array returned by the dashboard API. */
export interface RiskCountItem {
  status?: string;
  category?: string;
  impact?: string;
  count: number;
}

/**
 * Response shape for GET /api/core/v1/studies/{studyId}/risks/dashboard
 */
export interface RiskDashboardData {
  totalRisks: number;
  totalOpen: number;
  highRisks: number;
  overdue: number;
  byStatus: Record<string, number>; // e.g. { OPEN: 3, IN_PROGRESS: 1, UNDER_REVIEW: 0, CLOSED: 2 }
  byCategory: Record<string, number>; // e.g. { DATA_QUALITY: 2, ENROLLMENT: 1 }
  byImpact: Record<string, number>; // e.g. { LOW: 1, MEDIUM: 2, HIGH: 3 }
}

/** Query parameters for GET /api/core/v1/studies/{studyId}/risks */
export interface RiskListParams {
  page?: number;
  limit?: number;
  status?: string;
  category?: string;
  impact?: string;
  probability?: string;
  siteId?: string;
  owner?: string;
}

/** Response shape for GET /api/core/v1/studies/{studyId}/risks */
export interface RiskListResponse {
  items: Risk[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Request body for POST /api/core/v1/studies/{studyId}/risks
 * Enums sent as UPPERCASE (e.g. "HIGH", "ENROLLMENT")
 */
export interface CreateRiskRequest {
  title: string;
  description: string;
  category: string;
  studyId: string;
  siteId?: string;
  impact: string;
  probability: string;
  ownerId: string;
  dueDate: string;
  mitigationPlan?: string;
  sourceModule?: string;
  sourceEntityId?: string;
}

/** Minimal shape returned by the create API (id used to update local state) */
export interface CreateRiskResponse {
  id: string;
}

/**
 * Raw shape of a Risk returned by the list / get-by-id APIs.
 * Enums arrive as UPPERCASE_UNDERSCORE; relation IDs are UUIDs.
 * `fromApiRisk()` converts this to the display-oriented `Risk` type.
 */
export interface ApiRisk {
  id: string;
  riskNumber?: string; // e.g. "RISK-011"
  title: string;
  description?: string;
  category: string;
  studyId: string;
  studyTitle?: string; // human-readable study name
  siteId?: string;
  siteName?: string; // human-readable site name
  impact: string;
  probability: string;
  ownerId?: string;
  ownerName?: string;
  dueDate?: string;
  mitigationPlan?: string;
  sourceModule?: string;
  status?: string;
  riskScore?: number;
  active?: boolean;
  createdAt?: string;
  createdDate?: string;
  activityLog?: RiskActivityEntry[];
}

// ─── API → UI enum lookup tables ────────────────────────────────────────────

const _API_LEVEL: Record<string, RiskImpact> = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

const _API_STATUS: Record<string, RiskStatus> = {
  OPEN: RISK_STATUS.OPEN,
  IN_PROGRESS: RISK_STATUS.IN_PROGRESS,
  UNDER_REVIEW: RISK_STATUS.UNDER_REVIEW,
  MITIGATED: RISK_STATUS.MITIGATED,
  CLOSED: RISK_STATUS.CLOSED,
  OVERDUE: RISK_STATUS.OVERDUE,
};

export const _API_CATEGORY: Record<string, RiskCategory> = {
  ENROLLMENT: "Enrollment",
  SITE_PERFORMANCE: "Site Performance",
  MONITORING: "Monitoring",
  DATA_QUALITY: "Data Quality",
  PROTOCOL_COMPLIANCE: "Protocol Compliance",
  REGULATORY: "Regulatory",
  SAFETY: "Safety",
  DRUG_SUPPLY: "Drug Supply",
  FINANCIAL: "Financial",
  TMF_DOCUMENTATION: "TMF Documentation",
  OPERATIONAL_TIMELINE: "Operational Timeline",
  VENDOR_PARTNER: "Vendor / Partner",
  TECHNOLOGY: "Technology",
  QUALITY_CAPA: "Quality / CAPA",
};

const _API_SOURCE: Record<string, RiskSource> = {
  MANUAL: "Manual",
  EDC: "EDC",
  CDMS: "CDMS",
  MONITOR_TRACKER: "Monitor Tracker",
  REGULATORY: "Regulatory",
  ETMF: "eTMF",
  IWRS: "IWRS",
  FINANCE: "Finance",
};

/**
 * Map an `ApiRisk` (UPPERCASE enums, UUID relations) to the display `Risk` type.
 * Missing display-only fields (`study`, `site`, `ownerInitials`) are derived
 * from the available IDs/names so the UI always has something to render.
 */
export function fromApiRisk(r: ApiRisk): Risk {
  const impact: RiskImpact = _API_LEVEL[r.impact] ?? "Medium";
  const probability: RiskProbability = _API_LEVEL[r.probability] ?? "Medium";
  const score = r.riskScore ?? calcRiskScore(impact, probability);
  // Use ownerName only when non-empty string
  const owner = r.ownerName?.trim() ? r.ownerName.trim() : "";
  const ownerInitials = owner
    ? owner
        .split(" ")
        .map((w) => w[0] ?? "")
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";

  return {
    id: r.id,
    riskNumber: r.riskNumber,
    title: r.title,
    description: r.description ?? "",
    category: _API_CATEGORY[r.category] ?? "Monitoring",
    study: r.studyTitle ?? "",
    studyId: r.studyId,
    site: r.siteName ?? "",
    siteId: r.siteId ?? "",
    impact,
    probability,
    riskScore: score,
    riskLevel: getRiskLevel(score),
    owner,
    ownerId: r.ownerId,
    ownerInitials,
    status:
      _API_STATUS[r.status ?? ""] ??
      normalizeRiskStatus(r.status ?? RISK_STATUS.OPEN),
    createdDate: r.createdAt ?? r.createdDate ?? new Date().toISOString(),
    dueDate: r.dueDate ?? "",
    mitigationPlan: r.mitigationPlan ?? "",
    targetResolutionDate: r.dueDate ?? "",
    sourceModule: _API_SOURCE[r.sourceModule ?? ""] ?? "Manual",
    activityLog: r.activityLog ?? [],
  };
}

/**
 * Request body for PUT /api/core/v1/studies/{studyId}/risks/{riskId}
 * All fields are optional (partial update). Closed risks cannot be updated.
 * Enums sent as UPPERCASE (e.g. "HIGH", "DATA_QUALITY")
 */
export interface UpdateRiskRequest {
  title?: string;
  description?: string;
  category?: string;
  siteId?: string;
  impact?: string;
  probability?: string;
  ownerId?: string;
  dueDate?: string;
  mitigationPlan?: string;
  sourceModule?: string;
  sourceEntityId?: string;
}

/**
 * Request body for PATCH /api/core/v1/studies/{studyId}/risks/{riskId}/status
 */
export interface PatchRiskStatusRequest {
  status: string;
  notes: string;
}

/**
 * Allowed status transitions for the workflow UI.
 * Forward: OPEN → IN_PROGRESS → UNDER_REVIEW → CLOSED
 * Reverse: allowed except from CLOSED
 */
export const ALLOWED_TRANSITIONS: Partial<Record<RiskStatus, RiskStatus[]>> = {
  [RISK_STATUS.OPEN]: [RISK_STATUS.IN_PROGRESS],
  [RISK_STATUS.IN_PROGRESS]: [RISK_STATUS.UNDER_REVIEW, RISK_STATUS.OPEN],
  [RISK_STATUS.UNDER_REVIEW]: [RISK_STATUS.CLOSED, RISK_STATUS.IN_PROGRESS],
  [RISK_STATUS.CLOSED]: [],
  [RISK_STATUS.OVERDUE]: [RISK_STATUS.IN_PROGRESS],
  [RISK_STATUS.MITIGATED]: [],
};

/** Convert a display enum value (Title Case / spaces) to API UPPERCASE_UNDERSCORE */
export function toApiEnum(value: string): string {
  return value
    .toUpperCase()
    .replace(/[/\s]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Role-based permission matrix for Issue & Risk Tracker.
 *
 * TenantAdmin      – Oversight, escalation, closure
 * ProjectManager   – Create, assign, manage, close risks
 * CRA / Monitor    – Identify, update, execute mitigation
 * DataManager      – Create data-related risks, monitor queries
 * PrincipalInvestigator – View site risks (read-only)
 * StudyCoordinator – View risks (read-only)
 * Sponsor          – Portfolio-level oversight (read-only)
 * SiteAdmin        – View risks for their site (read-only)
 */
export const enum RiskTrackerRole {
  TenantAdmin = "TenantAdmin",
  ProjectManager = "ProjectManager",
  CRA = "CRA",
  DataManager = "DataManager",
  PrincipalInvestigator = "PrincipalInvestigator",
  StudyCoordinator = "StudyCoordinator",
  Sponsor = "Sponsor",
  SiteAdmin = "SiteAdmin",
}

export interface RiskPermissions {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canClose: boolean;
  canEscalate: boolean;
  readOnly: boolean;
}

export const ROLE_PERMISSIONS: Record<RiskTrackerRole, RiskPermissions> = {
  [RiskTrackerRole.TenantAdmin]: {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canClose: true,
    canEscalate: true,
    readOnly: false,
  },
  [RiskTrackerRole.ProjectManager]: {
    canCreate: true,
    canEdit: true,
    canDelete: false,
    canClose: true,
    canEscalate: true,
    readOnly: false,
  },
  [RiskTrackerRole.CRA]: {
    canCreate: true,
    canEdit: true,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: false,
  },
  [RiskTrackerRole.DataManager]: {
    canCreate: true,
    canEdit: true,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: false,
  },
  [RiskTrackerRole.PrincipalInvestigator]: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: true,
  },
  [RiskTrackerRole.StudyCoordinator]: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: true,
  },
  [RiskTrackerRole.Sponsor]: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: true,
  },
  [RiskTrackerRole.SiteAdmin]: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canClose: false,
    canEscalate: false,
    readOnly: true,
  },
};

export function getRiskPermissions(role: string): RiskPermissions {
  const normalized = normalizeToRiskRole(role);
  return (
    ROLE_PERMISSIONS[normalized] ??
    ROLE_PERMISSIONS[RiskTrackerRole.StudyCoordinator]
  );
}

const _USER_ROLE_TO_RISK_ROLE: Record<UserRoleEnum, RiskTrackerRole> = {
  [UserRoleEnum.TENANT]: RiskTrackerRole.TenantAdmin,
  [UserRoleEnum.PROJECT_MANAGER]: RiskTrackerRole.ProjectManager,
  [UserRoleEnum.CRA]: RiskTrackerRole.CRA,
  [UserRoleEnum.CTA]: RiskTrackerRole.CRA,
  [UserRoleEnum.DATA_MANAGER]: RiskTrackerRole.DataManager,
  [UserRoleEnum.PRINCIPAL_INVESTIGATOR]: RiskTrackerRole.PrincipalInvestigator,
  [UserRoleEnum.STUDY_COORDINATOR]: RiskTrackerRole.StudyCoordinator,
  [UserRoleEnum.SPONSOR]: RiskTrackerRole.Sponsor,
  [UserRoleEnum.SITE_ADMIN]: RiskTrackerRole.SiteAdmin,
  [UserRoleEnum.QA_QC]: RiskTrackerRole.StudyCoordinator,
  [UserRoleEnum.ACCOUNTANT]: RiskTrackerRole.StudyCoordinator,
  [UserRoleEnum.REGULATORY]: RiskTrackerRole.StudyCoordinator,
  [UserRoleEnum.FM]: RiskTrackerRole.StudyCoordinator,
};

export function normalizeToRiskRole(role: string): RiskTrackerRole {
  const userRole = normalizeRole(role);
  return _USER_ROLE_TO_RISK_ROLE[userRole] ?? RiskTrackerRole.StudyCoordinator;
}
